import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Send, Eye, Check, X } from 'lucide-react';
import { validate } from '../../validation/engine';
import {
    itaProgramService,
    ItaProgramDetail,
    ItaScoreRow,
} from '../../services/itaService';
import Modal from '../common/Modal';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import extractApiError from '../../utils/apiError';
import {
    SeamlessSurface,
    SeamlessCard,
    TypeChip,
    DualScorePanel,
    AssessRow,
    NetScore,
    StatusButtons,
    ResponseBar,
    SeamlessSkeleton,
    scoreBand,
} from '../audits/procedure-responses/seamless/SeamlessKit';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

// Match RAT Programs (RiskPrograms.tsx) — surface only In-Progress / Submitted
// / Closed. ITA's DB uses Not-Started, Complete (maker submitted) and Approved
// (checker signed off), so map those onto the RAT vocabulary.
const STATUS_LABELS: Record<string, string> = {
    'Not-Started': 'In-Progress',
    Complete: 'Submitted',
    Approved: 'Closed',
};
const statusLabel = (status?: string | null) =>
    STATUS_LABELS[status || ''] || status || 'In-Progress';

const statusBadgeClass = (status?: string | null) => {
    switch (statusLabel(status)) {
        case 'Closed':      return 'bg-green-100 text-green-700';
        case 'Submitted':   return 'bg-purple-100 text-purple-700';
        case 'In-Progress': return 'bg-blue-100 text-blue-700';
        default:            return 'bg-gray-100 text-gray-600';
    }
};

interface ITATExecutionModalProps {
    programId: number;
    onClose: () => void;
    onSaved?: () => void;
    readOnly?: boolean;
    /** Checker opens it to approve: inputs are locked, footer shows Approve. */
    isChecker?: boolean;
}

const ITATExecutionModal: React.FC<ITATExecutionModalProps> = ({
    programId,
    onClose,
    onSaved,
    readOnly = false,
    isChecker = false,
}) => {
    const { formatDate } = useEntitySettings();
    const [detail, setDetail] = useState<ItaProgramDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    // Remarks popup (parity with CAT/RAT) — carries its own commit callback.
    const [remarks, setRemarks] = useState<
        { title: string; value: string; testId?: string; onCommit: (v: string) => void } | null
    >(null);
    // Checker reject flow — needs a reason (sent to maker on bounce-back).
    const [showReject, setShowReject] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectErr, setRejectErr] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await itaProgramService.get(programId);
            setDetail(data);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load program') });
        } finally {
            setLoading(false);
        }
    }, [programId]);

    useEffect(() => { load(); }, [load]);

    // One PUT per score row, optimistic so the UI reflects the change immediately.
    const patchScore = async (
        row: ItaScoreRow,
        patch: {
            result?: 'Pass' | 'Fail' | 'NA' | null;
            effectiveness_score?: number | null;
            existence_score?: number | null;
            remarks?: string | null;
        },
    ) => {
        if (writeBlocked) return;
        setDetail(prev => prev ? {
            ...prev,
            scores: prev.scores.map(s => s.score_id === row.score_id ? { ...s, ...patch } : s),
        } : prev);
        try {
            await itaProgramService.updateScore(programId, row.score_id, patch);
        } catch (e: any) {
            // Rollback to the original row on failure.
            setDetail(prev => prev ? {
                ...prev,
                scores: prev.scores.map(s => s.score_id === row.score_id ? row : s),
            } : prev);
            setToast({ type: 'error', message: extractApiError(e, 'Failed to save') });
        }
    };

    const handleSubmit = async () => {
        if (writeBlocked) return;
        setSubmitting(true);
        try {
            await itaProgramService.submit(programId);
            setToast({ type: 'success', message: 'Program submitted for approval.' });
            onSaved?.();
            onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to submit') });
        } finally {
            setSubmitting(false);
        }
    };

    const handleApprove = async () => {
        if (!isChecker) return;
        setSubmitting(true);
        try {
            await itaProgramService.approve(programId);
            setToast({ type: 'success', message: 'Program approved — scores published.' });
            onSaved?.();
            onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to approve') });
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        if (!isChecker) return;
        const reason = rejectReason.trim();
        if (!reason) { setRejectErr('A reason is required to reject.'); return; }
        setSubmitting(true);
        try {
            await itaProgramService.reject(programId, reason);
            setToast({ type: 'success', message: 'Program rejected — sent back to the maker.' });
            setShowReject(false);
            setRejectReason('');
            onSaved?.();
            onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to reject') });
        } finally {
            setSubmitting(false);
        }
    };

    // Scores/results become read-only when: the viewer can't write (pure
    // viewer or checker — checker only approves), OR the maker has already
    // submitted (status Complete = submitted, Approved = signed off). After
    // submit the maker must not keep editing sliders/results — the whole
    // assessment is frozen until a checker bounces it back.
    const submitted = detail?.status === 'Complete' || detail?.status === 'Approved';
    const writeBlocked = readOnly || isChecker || submitted;

    // Each score row is one (control · implementation) item under test — present
    // each as its own card with Pass/Fail/NA + existence/effectiveness sliders.
    const rows = useMemo(() => detail?.scores ?? [], [detail]);
    const total = rows.length;
    // A row is "done" when it has a result AND both scores set.
    const isRowDone = (s: ItaScoreRow) =>
        !!s.result && s.effectiveness_score != null && s.effectiveness_score !== 0
        && s.existence_score != null && s.existence_score !== 0;
    const completed = rows.filter(isRowDone).length;
    const allDone = total > 0 && completed === total;

    const headerTitle = detail
        ? <>Assessment: "<span className="item-highlight">{detail.program_name}</span>" (IT Assessment)</>
        : 'Loading program…';

    const RESULT_OPTS: Array<{ val: string; label: string; mod: 'pass' | 'fail' | 'na' }> = [
        { val: 'Pass', label: 'Pass', mod: 'pass' },
        { val: 'Fail', label: 'Fail', mod: 'fail' },
        { val: 'NA', label: 'NA', mod: 'na' },
    ];

    return (
        <Modal isOpen onClose={onClose} size="xl" closeOnOverlayClick={false}>
            <Modal.Header>
                {headerTitle}
                {readOnly && (
                    <span className="status-badge sm neutral" style={{ marginLeft: 8 }}>
                        <Eye size={12} /> Read-only
                    </span>
                )}
                {!readOnly && isChecker && (
                    <span className="status-badge sm warning" style={{ marginLeft: 8 }}>
                        <Check size={12} /> Checker
                    </span>
                )}
            </Modal.Header>
            <Modal.Body>
                {loading && <SeamlessSkeleton />}
                {!loading && detail && (
                    <div className="collapse-section-stack">
                        <div className="response-shell-context">
                            {detail.template_code && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Template: </span>
                                    <span className="response-shell-meta-value">{detail.template_code}</span>
                                </span>
                            )}
                            {detail.target_label && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">
                                        {detail.target_type === 'IT_APPLICATION' ? 'Application: ' : 'Asset: '}
                                    </span>
                                    <span className="response-shell-meta-value">
                                        {detail.target_code ? `${detail.target_code} — ` : ''}{detail.target_label}
                                    </span>
                                </span>
                            )}
                            {detail.location_name && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Location: </span>
                                    <span className="response-shell-meta-value">{detail.location_name}</span>
                                </span>
                            )}
                            {detail.maker_name && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Maker: </span>
                                    <span className="response-shell-meta-value">{detail.maker_name}</span>
                                </span>
                            )}
                            {detail.checker_name && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Checker: </span>
                                    <span className="response-shell-meta-value">{detail.checker_name}</span>
                                </span>
                            )}
                            {(detail.start_date || detail.end_date) && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Period: </span>
                                    <span className="response-shell-meta-value">{formatDate(detail.start_date)} → {formatDate(detail.end_date)}</span>
                                </span>
                            )}
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(detail.status)}`}>
                                {statusLabel(detail.status)}
                            </span>
                            <span className="response-shell-meta-item" style={{ marginLeft: 'auto' }}>
                                <span className="response-shell-meta-value">{completed} / {total} scored</span>
                            </span>
                        </div>

                        {/* Checker's reject note — shown to the maker after a bounce-back
                            so they know what to fix. Hidden once re-approved. */}
                        {!isChecker && detail.rejection_reason && detail.status !== 'Approved' && (
                            <div className="response-inline-warning" style={{ margin: '0 0 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }} data-testid="BANNER_ita_reject_note">
                                <X size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                                <span><strong>Rejected by checker:</strong> {detail.rejection_reason}</span>
                            </div>
                        )}

                        <div className="seamless-tight">
                        <SeamlessSurface>
                            {rows.map((row, idx) => {
                                // Net Score = Existence × Effectiveness (out of 25). Unset
                                // sliders count as 0 — the score always shows a number
                                // (0/25 when zero), never "Not scored" (CAT parity).
                                const exV = row.existence_score ?? 0;
                                const efV = row.effectiveness_score ?? 0;
                                const net = exV * efV;
                                const variant = scoreBand(net);
                                const subParts = [row.implementation_type, row.explanation].filter(Boolean);
                                return (
                                    <SeamlessCard
                                        key={row.score_id}
                                        sno={idx + 1}
                                        snoLabel=""
                                        variant="control"
                                        title={row.control_name || row.control_code || `Control #${row.control_id}`}
                                        sub={subParts.length ? subParts.join(' · ') : null}
                                        typeChip={<TypeChip label={row.control_type} />}
                                        assessment={
                                            // Two stacked rows: row 1 = Result (Pass/Fail/NA,
                                            // the ITAT-only extra), row 2 = Ex + Ef sliders +
                                            // net-score verdict (identical to CAT control cards).
                                            <DualScorePanel
                                                row1={
                                                    <div className="seamless-assess__row" style={{ alignItems: 'center' }}>
                                                        <span className="seamless-assess__label">Result</span>
                                                        <StatusButtons
                                                            options={RESULT_OPTS}
                                                            value={row.result || ''}
                                                            disabled={writeBlocked}
                                                            onChange={(v) => patchScore(row, { result: (v || null) as 'Pass' | 'Fail' | 'NA' | null })}
                                                            testId={(val) => `BTN_ita_result_${row.score_id}_${val.toLowerCase()}`}
                                                        />
                                                    </div>
                                                }
                                                verdict1={null}
                                                row2={
                                                    <>
                                                        <AssessRow
                                                            label="Ex"
                                                            labelTitle="Existence"
                                                            value={row.existence_score ?? ''}
                                                            disabled={writeBlocked}
                                                            onChange={(v) => patchScore(row, { existence_score: v === '' ? null : (v as number) })}
                                                            testId={`SEL_ita_existence_${row.score_id}`}
                                                        />
                                                        <AssessRow
                                                            label="Ef"
                                                            labelTitle="Effectiveness"
                                                            value={row.effectiveness_score ?? ''}
                                                            disabled={writeBlocked}
                                                            onChange={(v) => patchScore(row, { effectiveness_score: v === '' ? null : (v as number) })}
                                                            testId={`SEL_ita_effectiveness_${row.score_id}`}
                                                        />
                                                    </>
                                                }
                                                verdict2={
                                                    <NetScore
                                                        text={`${net}/25`}
                                                        variant={variant}
                                                        title="Net Score = Existence × Effectiveness (out of 25)"
                                                        testId={`LBL_ita_net_score_${row.score_id}`}
                                                    />
                                                }
                                            />
                                        }
                                        actions={
                                            <ResponseBar
                                                note={{
                                                    label: 'Remarks',
                                                    filled: !!row.remarks,
                                                    onClick: () => setRemarks({
                                                        title: row.control_name || row.control_code || `Control #${row.control_id}`,
                                                        value: row.remarks || '',
                                                        testId: `INP_ita_remarks_${row.score_id}`,
                                                        onCommit: (text) => patchScore(row, { remarks: text }),
                                                    }),
                                                }}
                                            />
                                        }
                                    />
                                );
                            })}
                        </SeamlessSurface>
                        </div>
                    </div>
                )}
                {remarks && (
                    <RemarksModal
                        title={remarks.title}
                        initialValue={remarks.value}
                        readOnly={writeBlocked}
                        testId={remarks.testId}
                        onCommit={remarks.onCommit}
                        onClose={() => setRemarks(null)}
                    />
                )}
                <Toast toast={toast} onClose={() => setToast(null)} />
            </Modal.Body>
            {!readOnly && (
                <Modal.Footer>
                    {!isChecker && (
                        <Button
                            variant="fill"
                            icon={Send}
                            onClick={handleSubmit}
                            disabled={submitting || !allDone || detail?.status === 'Complete' || detail?.status === 'Approved'}
                            data-testid="BTN_ita_program_submit"
                        >
                            {submitting
                                ? 'Submitting…'
                                : detail?.status === 'Complete' || detail?.status === 'Approved'
                                    ? 'Submitted'
                                    : 'Submit for Approval'}
                        </Button>
                    )}
                    {isChecker && (
                        <>
                            <Button
                                variant="delete-outline"
                                icon={X}
                                onClick={() => { setRejectErr(null); setRejectReason(''); setShowReject(true); }}
                                disabled={submitting || detail?.status !== 'Complete'}
                                data-testid="BTN_ita_program_reject"
                            >
                                Reject
                            </Button>
                            <Button
                                variant="update"
                                icon={Check}
                                onClick={handleApprove}
                                disabled={submitting || detail?.status !== 'Complete'}
                                data-testid="BTN_ita_program_approve"
                            >
                                {submitting
                                    ? 'Approving…'
                                    : detail?.status === 'Approved'
                                        ? 'Approved'
                                        : 'Approve'}
                            </Button>
                        </>
                    )}
                </Modal.Footer>
            )}

            {showReject && (
                <Modal isOpen onClose={() => setShowReject(false)} size="md">
                    <Modal.Header onClose={() => setShowReject(false)}>Reject Assessment</Modal.Header>
                    <Modal.Body>
                        <p className="response-remarks-question">
                            Send this assessment back to the maker. State why it is being rejected.
                        </p>
                        <FormControl.Textarea
                            label=""
                            rows={5}
                            value={rejectReason}
                            placeholder="Reason for rejection (required)"
                            data-testid="INP_ita_program_reject_reason"
                            error={rejectErr || undefined}
                            autoFocus
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                                setRejectReason(e.target.value);
                                if (rejectErr) setRejectErr(null);
                            }}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="outline"
                            onClick={() => setShowReject(false)}
                            disabled={submitting}
                            data-testid="BTN_ita_program_reject_cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            icon={X}
                            onClick={handleReject}
                            disabled={submitting || !rejectReason.trim()}
                            data-testid="BTN_ita_program_reject_confirm"
                        >
                            {submitting ? 'Rejecting…' : 'Reject'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Modal>
    );
};

// Remarks popup for a score row (parity with CAT/RAT RemarksModal) — edits a
// local draft, validates against code 110, and commits (PUT) once on Done.
const RemarksModal: React.FC<{
    title: string;
    initialValue: string;
    readOnly?: boolean;
    testId?: string;
    onCommit: (text: string) => void;
    onClose: () => void;
}> = ({ title, initialValue, readOnly, testId, onCommit, onClose }) => {
    const [draft, setDraft] = useState(initialValue);
    const [err, setErr] = useState<string | null>(null);
    return (
        <Modal isOpen onClose={onClose} size="md">
            <Modal.Header onClose={onClose}>Remarks</Modal.Header>
            <Modal.Body>
                <p className="response-remarks-question">{title}</p>
                <FormControl.Textarea
                    label=""
                    rows={5}
                    value={draft}
                    disabled={readOnly}
                    placeholder="Observations (optional)"
                    data-testid={testId}
                    error={err || undefined}
                    autoFocus
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => { setDraft(e.target.value); if (err) setErr(null); }}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="fill"
                    disabled={readOnly}
                    onClick={() => {
                        if (draft && draft.trim()) {
                            const r = validate(110, draft);
                            if (!r.valid) { setErr(r.message ?? 'Invalid remarks.'); return; }
                        }
                        if (draft !== initialValue) onCommit(draft);
                        onClose();
                    }}
                >
                    Done
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ITATExecutionModal;
